import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import {
  cleanDateForApi,
  formatPhoneAsYouType,
  getMaxDateForInput,
  isMinor,
  parseInternationalPhone,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { clientApi } from '../services/clientApi';
import { InternationalPhoneInput, type InternationalPhoneChangeResult } from './InternationalPhoneInput';

interface LegalGuardianData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationshipToMinor: 'parent' | 'legal_guardian' | 'other';
  relationshipDetails?: string;
}

interface GuestClientData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor?: boolean | null;
  preferredLanguage: 'fr' | 'en';
  isAnonymous?: boolean;
  anonymousCode?: string;
  legalGuardian?: LegalGuardianData;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

interface GuestBookingFormProps {
  requireLegalGuardian: boolean;
  preferredLanguage: 'fr' | 'en';
  onComplete: (clientData: GuestClientData) => void;
  onBack?: () => void;
  initialValues?: Partial<GuestClientData>;
  anonymous?: boolean;
  hideNavigation?: boolean;
  formId?: string;
  onBusyChange?: (busy: boolean) => void;
}

const getPhoneState = (
  value: string,
  fallbackCountry: SupportedPhoneCountry = 'FR',
): InternationalPhoneChangeResult => {
  const parsed = parseInternationalPhone(value, fallbackCountry);
  const country = parsed.country ?? fallbackCountry;

  return {
    displayValue: formatPhoneAsYouType(value, country),
    e164Value: parsed.isValid ? parsed.e164 : null,
    country,
    isValid: parsed.isValid,
  };
};

export function GuestBookingForm({
  requireLegalGuardian,
  preferredLanguage,
  onComplete,
  onBack,
  initialValues,
  anonymous = false,
  hideNavigation = false,
  formId,
  onBusyChange,
}: GuestBookingFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null as boolean | null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    legalGuardian: null as LegalGuardianData | null
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [phoneState, setPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [guardianPhoneState, setGuardianPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [emergencyPhoneState, setEmergencyPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));

  useEffect(() => {
    onBusyChange?.(loading);
    return () => onBusyChange?.(false);
  }, [loading, onBusyChange]);

  const clientIsMinor = formData.dateOfBirth
    ? isMinor(formData.dateOfBirth)
    : formData.isMinor;
  const showGuardian = requireLegalGuardian && clientIsMinor;

  // Prefill with provided initial values (e.g., email captured earlier)
  useEffect(() => {
    if (!initialValues) return;
    const nextPhoneState = initialValues.phone !== undefined ? getPhoneState(initialValues.phone) : null;
    const nextGuardianPhoneState = initialValues.legalGuardian?.phone !== undefined
      ? getPhoneState(initialValues.legalGuardian.phone)
      : null;
    const nextEmergencyPhoneState = initialValues.emergencyContact?.phone !== undefined
      ? getPhoneState(initialValues.emergencyContact.phone)
      : null;
    if (nextPhoneState) setPhoneState(nextPhoneState);
    if (nextGuardianPhoneState) setGuardianPhoneState(nextGuardianPhoneState);
    if (nextEmergencyPhoneState) setEmergencyPhoneState(nextEmergencyPhoneState);
    setFormData(prev => ({
      ...prev,
      firstName: initialValues.firstName ?? prev.firstName,
      lastName: initialValues.lastName ?? prev.lastName,
      email: initialValues.email ?? prev.email,
      phone: nextPhoneState?.displayValue ?? prev.phone,
      dateOfBirth: initialValues.dateOfBirth ?? prev.dateOfBirth,
      emergencyContact: initialValues.emergencyContact
        ? {
            name: initialValues.emergencyContact.name ?? prev.emergencyContact.name,
            phone: nextEmergencyPhoneState?.displayValue ?? prev.emergencyContact.phone,
            relationship: initialValues.emergencyContact.relationship ?? prev.emergencyContact.relationship
          }
        : prev.emergencyContact,
      legalGuardian: initialValues.legalGuardian
        ? { ...initialValues.legalGuardian, phone: nextGuardianPhoneState?.displayValue ?? initialValues.legalGuardian.phone }
        : prev.legalGuardian
    }));
  }, [initialValues]);

  // Check for existing client when email/phone changes
  useEffect(() => {
    const checkClient = async () => {
      if (!formData.email && !formData.phone) {
        setEmailExists(false);
        setPhoneExists(false);
        return;
      }

      if (formData.email) {
        setCheckingEmail(true);
        try {
          const response = await clientApi.checkDuplicate({ email: formData.email, phone: '' });
          if (response.exists && (response.fields?.includes('email') || response.field === 'email')) {
            setEmailExists(true);
          } else {
            setEmailExists(false);
          }
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setCheckingEmail(false);
        }
      }

      if (phoneState.e164Value) {
        setCheckingPhone(true);
        try {
          const response = await clientApi.checkDuplicate({ email: '', phone: phoneState.e164Value });
          if (response.exists && (response.fields?.includes('phone') || response.field === 'phone')) {
            setPhoneExists(true);
          } else {
            setPhoneExists(false);
          }
        } catch (error) {
          console.error('Error checking phone:', error);
        } finally {
          setCheckingPhone(false);
        }
      }
    };

    const timeoutId = setTimeout(checkClient, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [formData.email, phoneState.e164Value]);

  // Update legal guardian when minor status changes
  useEffect(() => {
    if (showGuardian && !formData.legalGuardian) {
      setFormData(prev => ({
        ...prev,
        legalGuardian: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          relationshipToMinor: 'parent',
          relationshipDetails: ''
        }
      }));
    } else if (!showGuardian) {
      setFormData(prev => ({ ...prev, legalGuardian: null }));
    }
  }, [showGuardian]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('customerDetails.guest.errors.firstNameRequired');
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('customerDetails.guest.errors.emailInvalid');
    }
    if (phoneState.displayValue.trim() && (!phoneState.isValid || !phoneState.e164Value)) {
      newErrors.phone = t('auth.register.errors.invalidPhone');
    }

    if (showGuardian && formData.legalGuardian) {
      if (
        formData.legalGuardian.email.trim()
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.legalGuardian.email)
      ) {
        newErrors['legalGuardian.email'] = t('customerDetails.guest.errors.guardianEmailInvalid');
      }
      if (guardianPhoneState.displayValue.trim() && (!guardianPhoneState.isValid || !guardianPhoneState.e164Value)) {
        newErrors['legalGuardian.phone'] = t('auth.register.errors.invalidPhone');
      }
    }

    if (emergencyPhoneState.displayValue.trim() && !emergencyPhoneState.isValid) {
      newErrors['emergencyContact.phone'] = t('auth.register.errors.invalidPhone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Create minimal client via API
      const trimmedEmail = formData.email.trim();
      const trimmedPhone = phoneState.e164Value ?? '';
      const guardianPhone = guardianPhoneState.e164Value ?? '';
      const emergencyPhone = emergencyPhoneState.e164Value ?? '';
      const clientData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: trimmedEmail,
        phone: trimmedPhone,
        dateOfBirth: cleanDateForApi(formData.dateOfBirth) || '',
        isMinor: clientIsMinor,
        address: {
          // Minimal placeholder address to satisfy required fields on API side
          street: 'Adresse non fournie',
          city: 'Ville non fournie',
          postalCode: '00000',
          country: 'France'
        },
        preferredLanguage,
        requireLegalGuardian,
        legalGuardian: showGuardian && formData.legalGuardian ? {
          firstName: formData.legalGuardian.firstName.trim(),
          lastName: formData.legalGuardian.lastName.trim(),
          email: formData.legalGuardian.email.trim(),
          phone: guardianPhone,
          relationshipToMinor: formData.legalGuardian.relationshipToMinor,
          relationshipDetails: formData.legalGuardian.relationshipDetails
        } : undefined,
        emergencyContact: {
          name: formData.emergencyContact.name.trim(),
          phone: emergencyPhone,
          relationship: formData.emergencyContact.relationship.trim(),
        },
        consents: {
          dataProcessing: true,
          photography: false,
          marketing: false
        }
      };

      const response = await fetch('/api/clients/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData),
      });

      const data = await response.json();

      if (data.success && data.data?.client) {
        onComplete({
          id: data.data.client.id,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: trimmedEmail,
          phone: trimmedPhone,
          dateOfBirth: formData.dateOfBirth,
          isMinor: clientIsMinor,
          preferredLanguage,
          legalGuardian: formData.legalGuardian
            ? {
                ...formData.legalGuardian,
                firstName: formData.legalGuardian.firstName.trim(),
                lastName: formData.legalGuardian.lastName.trim(),
                email: formData.legalGuardian.email.trim(),
                phone: guardianPhone,
              }
            : undefined,
          emergencyContact: {
            name: formData.emergencyContact.name.trim(),
            phone: emergencyPhone,
            relationship: formData.emergencyContact.relationship.trim(),
          },
        });
      } else {
        if (data.errors) {
          const errorMap: Record<string, string> = {};
          data.errors.forEach((err: any) => {
            errorMap[err.field] = err.message;
          });
          setErrors(errorMap);
        } else {
          setErrors({ general: data.message || t('customerDetails.guest.errors.createFailed') });
        }
      }
    } catch (error) {
      console.error('Guest client creation error:', error);
      setErrors({ general: t('customerDetails.guest.errors.networkError') });
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousSubmit = async () => {
    if (loading) return;

    setLoading(true);
    setErrors({});

    try {
      const firstName = formData.firstName.trim();
      const response = await clientApi.createAnonymous({
        firstName: firstName || undefined,
        preferredLanguage,
      });

      if (response.success && response.data?.client?.id) {
        onComplete({
          id: response.data.client.id,
          firstName: response.data.client.firstName || firstName,
          lastName: response.data.client.lastName || '',
          email: response.data.client.email || '',
          phone: response.data.client.phone || '',
          dateOfBirth: '',
          preferredLanguage,
          isAnonymous: true,
          anonymousCode: response.data.client.anonymousCode,
        });
        return;
      }

      setErrors({
        general: response.message || t('customerDetails.guest.errors.createFailed'),
      });
    } catch (error) {
      const apiError = error as { data?: { message?: string } };
      console.error('Anonymous client creation error:', error);
      setErrors({
        general: apiError.data?.message || t('customerDetails.guest.errors.networkError'),
      });
    } finally {
      setLoading(false);
    }
  };

  if (anonymous) {
    return (
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          void handleAnonymousSubmit();
        }}
        className={`space-y-6 ${loading ? 'opacity-60' : ''}`}
      >
        <fieldset disabled={loading} className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2">{t('customerDetails.anonymous.title')}</h2>
          <p className="text-gray-600">{t('customerDetails.anonymous.subtitle')}</p>
        </div>

        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{errors.general}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">
            {t('customerDetails.anonymous.firstName')}
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
            onBlur={() => setFormData((prev) => ({ ...prev, firstName: prev.firstName.trim() }))}
            className="w-full p-3 border rounded-lg"
            placeholder={t('customerDetails.anonymous.firstNamePlaceholder')}
          />
          <p className="text-sm text-gray-500 mt-1">{t('customerDetails.anonymous.firstNameHint')}</p>
        </div>
        </fieldset>

        {!hideNavigation && (
        <div className="flex justify-between pt-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 border rounded-lg hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ml-auto"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('customerDetails.guest.creating')}</span>
              </>
            ) : (
              <span>{t('common.next')}</span>
            )}
          </button>
        </div>
        )}
      </form>
    );
  }

  return (
    <fieldset disabled={loading} className={`space-y-6 ${loading ? 'opacity-60' : ''}`}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">{t('customerDetails.guest.title')}</h2>
        <p className="text-gray-600">{t('customerDetails.guest.subtitle')}</p>
      </div>

      {/* Warning if email/phone exists */}
      {(emailExists || phoneExists) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">{t('customerDetails.guest.warning.title')}</p>
              <p className="text-sm text-yellow-700 mt-1">
                {emailExists && phoneExists
                  ? t('customerDetails.guest.warning.bothExist')
                  : emailExists
                  ? t('customerDetails.guest.warning.emailExists')
                  : t('customerDetails.guest.warning.phoneExists')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* General error */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{errors.general}</p>
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg flex items-center space-x-2">
          <User className="w-5 h-5" />
          <span>{t('customerDetails.guest.basicInfo')}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.firstName')} *</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              onBlur={() => setFormData(prev => ({ ...prev, firstName: prev.firstName.trim() }))}
              className={`w-full p-3 border rounded-lg ${errors.firstName ? 'border-red-500' : ''}`}
              placeholder={t('customerDetails.guest.firstNamePlaceholder')}
            />
            {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.lastName')} (optionnel)</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              onBlur={() => setFormData(prev => ({ ...prev, lastName: prev.lastName.trim() }))}
              className={`w-full p-3 border rounded-lg ${errors.lastName ? 'border-red-500' : ''}`}
              placeholder={t('customerDetails.guest.lastNamePlaceholder')}
            />
            {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>{t('customerDetails.guest.email')} (optionnel)</span>
            {checkingEmail && <Loader2 className="w-4 h-4 animate-spin" />}
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            onBlur={() => setFormData(prev => ({ ...prev, email: prev.email.trim() }))}
            className={`w-full p-3 border rounded-lg ${errors.email || emailExists ? 'border-red-500' : ''}`}
            placeholder={t('customerDetails.guest.emailPlaceholder')}
          />
            {(errors.email || emailExists) && (
              <p className="text-sm text-red-600 mt-1">
                {errors.email || t('customerDetails.guest.warning.emailExists')}
              </p>
            )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 flex items-center space-x-2">
            <Phone className="w-4 h-4" />
            <span>{t('customerDetails.guest.phone')} (optionnel)</span>
            {checkingPhone && <Loader2 className="w-4 h-4 animate-spin" />}
          </label>
          <InternationalPhoneInput
            value={phoneState.displayValue}
            country={phoneState.country}
            onChange={(result) => {
              setPhoneState(result);
              setFormData((prev) => ({ ...prev, phone: result.displayValue }));
              if (result.isValid && errors.phone) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.phone;
                  return next;
                });
              }
            }}
            selectClassName="w-full p-3 border rounded-lg border-gray-300"
            inputClassName={`w-full p-3 border rounded-lg ${errors.phone || phoneExists ? 'border-red-500' : ''}`}
            placeholder={t('customerDetails.guest.phonePlaceholder')}
            error={errors.phone || (phoneExists ? t('customerDetails.guest.warning.phoneExists') : undefined)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 flex items-center space-x-2">
            <Calendar className="w-4 h-4" />
            <span>{t('customerDetails.guest.dateOfBirth')} (optionnel)</span>
          </label>
          <input
            type="date"
            value={formData.dateOfBirth}
            disabled={formData.isMinor === true && !formData.dateOfBirth}
            onChange={(e) => {
              const value = e.target.value;
              setFormData(prev => ({
                ...prev,
                dateOfBirth: value,
                isMinor: value ? isMinor(value) : null,
              }));
            }}
            max={getMaxDateForInput()}
            className={`w-full p-3 border rounded-lg ${errors.dateOfBirth ? 'border-red-500' : ''}`}
          />
          {errors.dateOfBirth && <p className="text-sm text-red-600 mt-1">{errors.dateOfBirth}</p>}
          {clientIsMinor && (
            <p className="text-sm text-blue-600 mt-1">{t('customerDetails.guest.minorDetected')}</p>
          )}
          <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
            <input
              type="checkbox"
              checked={clientIsMinor}
              disabled={Boolean(formData.dateOfBirth)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isMinor: e.target.checked ? true : null,
                }))
              }
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Mineur (optionnel)
          </label>
        </div>
      </div>

      {/* Legal Guardian Section */}
      {showGuardian && formData.legalGuardian && (
        <div className="space-y-4 border-t pt-6">
          <h3 className="font-semibold text-lg">{t('customerDetails.guest.legalGuardian')}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.guardianFirstName')}</label>
              <input
                type="text"
                value={formData.legalGuardian.firstName}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, firstName: e.target.value } : null
                }))}
                onBlur={() => setFormData(prev => ({
                  ...prev,
                  legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, firstName: prev.legalGuardian.firstName.trim() } : null
                }))}
                className={`w-full p-3 border rounded-lg ${errors['legalGuardian.firstName'] ? 'border-red-500' : ''}`}
              />
              {errors['legalGuardian.firstName'] && <p className="text-sm text-red-600 mt-1">{errors['legalGuardian.firstName']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.guardianLastName')}</label>
              <input
                type="text"
                value={formData.legalGuardian.lastName}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, lastName: e.target.value } : null
                }))}
                onBlur={() => setFormData(prev => ({
                  ...prev,
                  legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, lastName: prev.legalGuardian.lastName.trim() } : null
                }))}
                className={`w-full p-3 border rounded-lg ${errors['legalGuardian.lastName'] ? 'border-red-500' : ''}`}
              />
              {errors['legalGuardian.lastName'] && <p className="text-sm text-red-600 mt-1">{errors['legalGuardian.lastName']}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.guardianEmail')}</label>
            <input
              type="email"
              value={formData.legalGuardian.email}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, email: e.target.value } : null
              }))}
              onBlur={() => setFormData(prev => ({
                ...prev,
                legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, email: prev.legalGuardian.email.trim() } : null
              }))}
              className={`w-full p-3 border rounded-lg ${errors['legalGuardian.email'] ? 'border-red-500' : ''}`}
            />
            {errors['legalGuardian.email'] && <p className="text-sm text-red-600 mt-1">{errors['legalGuardian.email']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.guardianPhone')}</label>
            <InternationalPhoneInput
              value={guardianPhoneState.displayValue}
              country={guardianPhoneState.country}
              onChange={(result) => {
                setGuardianPhoneState(result);
                setFormData((prev) => ({
                  ...prev,
                  legalGuardian: prev.legalGuardian
                    ? { ...prev.legalGuardian, phone: result.displayValue }
                    : null,
                }));
                if (result.isValid && errors['legalGuardian.phone']) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['legalGuardian.phone'];
                    return next;
                  });
                }
              }}
              selectClassName="w-full p-3 border rounded-lg border-gray-300"
              inputClassName={`w-full p-3 border rounded-lg ${errors['legalGuardian.phone'] ? 'border-red-500' : ''}`}
              error={errors['legalGuardian.phone']}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('customerDetails.guest.relationship')}</label>
            <select
              value={formData.legalGuardian.relationshipToMinor}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, relationshipToMinor: e.target.value as any } : null
              }))}
              className="w-full p-3 border rounded-lg"
            >
              <option value="parent">{t('customerDetails.guest.relationshipOptions.parent')}</option>
              <option value="legal_guardian">{t('customerDetails.guest.relationshipOptions.legalGuardian')}</option>
              <option value="other">{t('customerDetails.guest.relationshipOptions.other')}</option>
            </select>
          </div>
        </div>
      )}

      {/* Emergency Contact */}
      <div className="space-y-4 border-t pt-6">
        <h3 className="font-semibold text-lg">{t('customerDetails.guest.emergencyContact')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('customerDetails.guest.emergencyName')}
            </label>
            <input
              type="text"
              value={formData.emergencyContact.name}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                emergencyContact: { ...prev.emergencyContact, name: e.target.value }
              }))}
              onBlur={() => setFormData(prev => ({
                ...prev,
                emergencyContact: { ...prev.emergencyContact, name: prev.emergencyContact.name.trim() }
              }))}
              className="w-full p-3 border rounded-lg"
              placeholder={t('customerDetails.guest.emergencyNamePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('customerDetails.guest.emergencyPhone')}
            </label>
            <InternationalPhoneInput
              value={emergencyPhoneState.displayValue}
              country={emergencyPhoneState.country}
              onChange={(result) => {
                setEmergencyPhoneState(result);
                setFormData((prev) => ({
                  ...prev,
                  emergencyContact: { ...prev.emergencyContact, phone: result.displayValue },
                }));
                if (result.isValid && errors['emergencyContact.phone']) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['emergencyContact.phone'];
                    return next;
                  });
                }
              }}
              selectClassName="w-full p-3 border rounded-lg border-gray-300"
              inputClassName={`w-full p-3 border rounded-lg ${errors['emergencyContact.phone'] ? 'border-red-500' : ''}`}
              placeholder={t('customerDetails.guest.emergencyPhonePlaceholder')}
              error={errors['emergencyContact.phone']}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('customerDetails.guest.emergencyRelationship')}
            </label>
            <input
              type="text"
              value={formData.emergencyContact.relationship}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                emergencyContact: { ...prev.emergencyContact, relationship: e.target.value }
              }))}
              onBlur={() => setFormData(prev => ({
                ...prev,
                emergencyContact: { ...prev.emergencyContact, relationship: prev.emergencyContact.relationship.trim() }
              }))}
              className="w-full p-3 border rounded-lg"
              placeholder={t('customerDetails.guest.emergencyRelationshipPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        {onBack && (
          <button
            onClick={onBack}
            className="px-6 py-3 border rounded-lg hover:bg-gray-50"
          >
            {t('common.previous')}
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ml-auto"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('customerDetails.guest.creating')}</span>
            </>
          ) : (
            <span>{t('common.next')}</span>
          )}
        </button>
      </div>
    </fieldset>
  );
}
